/**
 * Prioritizes tags based on active filters
 * - When filters are active, filtered tags appear first
 * - Within each group (filtered/unfiltered), tags are alphabetically sorted
 * - Case-insensitive matching for filter comparison
 *
 * @param tags - All tags for the vulnerability
 * @param activeFilters - Currently selected tag filters (undefined if no filters)
 * @returns Sorted array with filtered tags first, then remaining tags
 *
 * @example
 * getPrioritizedTags(['zebra', 'apple', 'banana'], ['banana'])
 * // Returns: ['banana', 'apple', 'zebra']
 */
export const getPrioritizedTags = (
  tags: readonly string[],
  activeFilters?: string[]
): string[] => {
  // No filters active - just alphabetical sort
  if (!activeFilters || activeFilters.length === 0) {
    return [...tags].sort();
  }

  // Normalize filters for case-insensitive matching
  const normalizedFilters = activeFilters.map(filter => filter.toLowerCase());
  const filteredSet = new Set(normalizedFilters);

  // Separate matched and unmatched tags
  const matchedTags = tags
    .filter(tag => filteredSet.has(tag.toLowerCase()))
    .sort();

  const unmatchedTags = tags
    .filter(tag => !filteredSet.has(tag.toLowerCase()))
    .sort();

  // Matched tags first, then unmatched
  return [...matchedTags, ...unmatchedTags];
};
